import { captureException } from '@sentry/nextjs'
import { useEffect, useState } from 'react'
import 'project/polyfill'
import { I18NConfig } from 'next/dist/server/config-shared'

import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import { sdk } from 'common/sdk'
import Strings from 'project/localisation'
import Carousel from 'components/Carousel'
import Button from 'components/forms/Button'
import { NextPageWithLayout } from '../../types/nextPageWithLayout'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import { marketplaceStrings } from 'common/strings/marketplace-strings'
import { Offer, OfferSummary } from 'common/types/responses'
import OfferDetailHeroSection from 'components/marketplace/OfferDetailHeroSection'
import { businessBankingStrings } from 'common/strings/business-banking-strings'
import ModalDefault from 'components/Modal'
import { Format, Utils } from 'common/utils'
import { Project } from 'common/project'
import usePreview from 'project/usePreview'
import Link from 'next/link'
import uniqBy from 'lodash/uniqBy'
import standardiseTitle from 'project/standardiseTitle'
import imageResize from 'project/image-proxy-web'

export type OfferDetailPageProps = {
  offerDetail: Offer
  relatedOffers?: OfferSummary[]
}
const OfferDetailPage: NextPageWithLayout<OfferDetailPageProps> = ({
  offerDetail: _offerDetail,
  relatedOffers,
}) => {
  const [offerDetail, setOfferDetail] = useState(_offerDetail)
  const { preview } = usePreview()
  useEffect(() => {
    if (preview) {
      getData(offerDetail.slug).then((res) => {
        setOfferDetail(res.offerDetail!)
      })
    }
  }, [preview])
  const seoProps: SeoContainerProps['seoProps'] = {
    description:
      _offerDetail.metadata_description || _offerDetail.hero_description,
    openGraph: {
      images: _offerDetail.brand.logo?.url
        ? [{ url: _offerDetail.brand.logo.url }]
        : undefined,
      url: `https://www.hoxtonmix.com/marketplace/${offerDetail.slug}/`,
    },
    title: standardiseTitle(
      `${
        _offerDetail.metadata_title ||
        `${_offerDetail.title} - ${_offerDetail.brand.name}`
      } | Hoxton Mix`,
    ),
  }
  const breadcrumb = homeBreadcrumb().concat([
    {
      item: `https://www.hoxtonmix.com/marketplace/${offerDetail.slug}/`,
      name: `${Strings.offers}`,
      position: 2,
    },
  ])

  const [termsOpen, setTermsOpen] = useState(false)
  const toggleTerms = () => {
    setTermsOpen(!termsOpen)
  }

  const handleUnlockDeal = () => {
    if (offerDetail.secured) {
      window.location.href = `${Project.portalURL}/marketplace/${offerDetail.id}`
    } else {
      sdk.createOfferRedemption({ id: offerDetail.id }).then((res) => {
        window.location.href = res.url
      })
    }
  }

  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <main>
        <OfferDetailHeroSection offerDetail={offerDetail} />

        <div className='container px-xxl-52 px-lg-28 px-5 my-lg-25 my-16'>
          <div className='d-flex align-items-center mb-4'>
            <img
              src={imageResize(
                require('public/images/icons/benefit-value-icon.png'),
              )}
              alt={marketplaceStrings.benefitValue}
              className={'me-4'}
            />
            <p className='fs-fluid-24-32 mb-0'>
              {marketplaceStrings.benefitValue}
            </p>
          </div>
          <h1 className={'pe-lg-18'}>{offerDetail.title}</h1>
          {offerDetail?.secured ? (
            <div className={'d-flex align-items-center'}>
              <img
                src={imageResize(
                  require('public/images/icons/lock_person.png'),
                )}
                alt={marketplaceStrings.loginToRedeemOffer}
                className={'me-4'}
              />
              <p className='fs-fluid-18-24 mb-0'>
                {marketplaceStrings.loginToRedeemOffer}
              </p>
            </div>
          ) : (
            !!offerDetail.requirements?.[0] && (
              <p className='fs-fluid-18-24 mb-0'>
                {marketplaceStrings.formatString(
                  marketplaceStrings.offerAvailabilityCondition,
                  Format.capitalizeFirstLetter(
                    offerDetail.requirements?.[0] ?? '',
                  ),
                )}
              </p>
            )
          )}

          {!!offerDetail.metadata_title && (
            <h2 className='fs-fluid-24-32 text-semibold my-8'>
              {offerDetail.metadata_title}
            </h2>
          )}
          <Button
            theme='marketplace'
            onClick={handleUnlockDeal}
            className={
              'text-xl-2 text-semibold rounded rounded-5 py-0 ps-8 pe-2'
            }
          >
            <span className={'me-12'}>{marketplaceStrings.UnlockThisDeal}</span>
          </Button>
        </div>
        <div className='bg-grey6 pt-lg-10 pb-lg-25 pt-1 pb-16'>
          <div className='offer-main-container container px-xxl-52 px-lg-28 px-5'>
            <div
              dangerouslySetInnerHTML={{
                __html: offerDetail.content,
              }}
            ></div>
            {!!offerDetail.terms_content && (
              <div className={'fs-fluid-18-24 color-black mb-10 mt-8'}>
                <a
                  onClick={toggleTerms}
                  className='cursor-pointer text-blue text-decoration-none'
                >
                  {Strings.clickHere} {businessBankingStrings.forTheOfferTerms}
                </a>
              </div>
            )}
            <Button
              theme='marketplace'
              onClick={handleUnlockDeal}
              className={
                'text-xl-2 text-semibold rounded rounded-5 py-0 ps-8 pe-2'
              }
            >
              <span className={'me-12'}>
                {marketplaceStrings.UnlockThisDeal}
              </span>
            </Button>
          </div>
        </div>

        <div className='container px-xxl-52 px-lg-28 px-5 my-lg-25 my-16'>
          {!!offerDetail.screenshots?.length && (
            <>
              <div className='d-flex flex-column mb-8 px-lg-0 mt-lg-27 mt-18 px-6'>
                <div className='marketplace-blog-container blog-container overflow-x-hidden'>
                  <span className='text-xxl text-bold'>
                    {offerDetail.brand.name} - {marketplaceStrings.screenshots}
                  </span>
                  <Carousel
                    uncontrolled
                    slideClass={'blog-item__container mt-10'}
                  >
                    {offerDetail.screenshots.map((screenshotImage, index) => (
                      <img
                        key={screenshotImage.url || index}
                        style={{ width: '100%' }}
                        className='img-fluid'
                        src={screenshotImage.url}
                        alt={`screenshot ${index + 1}`}
                        sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                      />
                    ))}
                  </Carousel>
                </div>
              </div>
              <div className={'border border-b my-lg-13 my-9'}></div>
            </>
          )}

          {!!Project.flags.related_offers && !!relatedOffers?.length && (
            <div>
              <h3 className='fs-fluid-24-32 text-semibold mb-18'>
                {marketplaceStrings.peopleAlsoLikedTheseOffers}
              </h3>
              <div className='row'>
                {relatedOffers?.map((offer) => (
                  <div key={offer.id} className='col-lg-6 mb-4'>
                    <Link
                      className='fs-fluid-18-24 text-decoration-none color-black2 fw-semibold'
                      href={Utils.getOfferUrl(offer)}
                    >
                      {offer.metadata_title || offer.title}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <ModalDefault isOpen={termsOpen} onDismiss={() => setTermsOpen(false)}>
          <h3 className={'h4--small d-flex align-items-center'}>
            {businessBankingStrings.offerTermsAndConditions}
          </h3>
          <div
            dangerouslySetInnerHTML={{
              __html: `${offerDetail.terms_content}`,
            }}
          />
        </ModalDefault>
      </main>
    </SeoContainer>
  )
}

export default OfferDetailPage

const getData = async (offerSlug: string) => {
  const [offerDetail, allOffersResponse, featuredResponse] = await Promise.all([
    sdk.getOffer({ slug: offerSlug }),
    sdk.getAllOffers(),
    sdk.getAllOffers({ featured: true }),
  ])

  const allOffers = allOffersResponse?.results ?? []
  const featuredOffers = featuredResponse?.results ?? []

  // Prioritize featured offers, then fill with other offers
  const relatedOffers = uniqBy(
    [...featuredOffers, ...allOffers].filter(
      (offer) => offer != null && offer.slug !== offerSlug,
    ),
    'id',
  ).slice(0, 10)

  return { offerDetail, relatedOffers }
}

const offerToPath = (offer: OfferSummary) => {
  if (!offer.slug) {
    throw new Error(
      `Offer ${offer.id} does not have a slug, you need to re-save it`,
    )
  }
  return { params: { offerSlug: offer.slug } }
}

export async function getStaticPaths(_context: I18NConfig) {
  const limit = process.env.OFFER_LIMIT
    ? parseInt(process.env.OFFER_LIMIT, 10)
    : undefined

  if (limit) {
    console.log(`OFFER_LIMIT=${limit} - Building only ${limit} offer page(s)`)
  }

  const offers = limit
    ? await sdk.getOffers({ page: 1, page_size: limit })
    : await sdk.getAllOffers()

  const paths = (offers.results ?? []).filter(Boolean).map(offerToPath)

  return { fallback: false, paths }
}

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { offerSlug: string } },
) {
  const { offerSlug } = context.params

  try {
    const { offerDetail, relatedOffers } = await getData(offerSlug)

    if (!offerDetail) {
      return { notFound: true }
    }

    return {
      props: { offerDetail, relatedOffers },
    }
  } catch (e) {
    captureException(e, { tags: { offerSlug, url: `/marketplace/${offerSlug}` } })
    throw e
  }
}
